import { useEffect, useRef, useState } from 'react';
import { api } from '../api.ts';
import Tiefbaukarte, {
  type Flurstueck, type Kartenlage, type Punkt,
} from './Tiefbaukarte.tsx';
import { kartenmass } from './kartenmass.ts';
/* DIE VIER HAKEN STEHEN OHNE JSX DANEBEN. Sie entscheiden seit .144 auch,
   was auf dem BLATT an die Gemeinde steht; die zweite Fassung derselben vier
   Werte liegt im Server (`regeln/blattlagen.ts`), und Prüfstand 125 legt sie
   gegeneinander - eine Datei mit JSX lässt sich dafür nicht laden. */
import type { Kartenschalter } from './kartenstart.ts';

/**
 * EINE KARTE FÜR BEIDE BEREICHE.
 *
 * ORIGINALTEXT MARVIN (04.09.2026)
 *   „also nochmal zur klarheit. beide Bereiche mit Lageplan vorbereiten für
 *    die gemeinde und planung für den tiefbau hat die gleiche kartenstruktur
 *    mit allem. es unterscheidet sich nur in dem was markiert ist beim starten
 *    und die symbole zur anzeichnung! das ist derzeit 2 unterschiedliche
 *    karten."
 *
 * **ER HAT RECHT, UND ES WAR NACHZUZÄHLEN.** Der Lageplan und die
 * Tiefbauplanung trugen bis .107 jeder seine eigene Fassung von genau
 * denselben fünf Dingen:
 *
 * ```
 *   die vier Schalter          zweimal, mit VERSCHIEDENEN Aufschriften
 *                              („Grundstücksgrenzen" gegen „Grenzen")
 *   das Nachladen der Grenzen  zweimal, Zeile für Zeile gleich - mit einem
 *                              Kommentar daneben, der das sogar zugibt
 *   `kasten()` / `kartenkasten()`  zweimal, Zeichen für Zeichen dieselbe
 *                              Rechnung unter zwei Namen
 *   das Messen der Spalte      an drei Stellen (hier, in der Planung, in
 *                              `Vorbereiten.tsx`)
 *   der Aufruf von `Tiefbaukarte`  zweimal, mit je zehn Vorlagen
 * ```
 *
 * **ZWEI ABSCHRIFTEN SIND ZWEI WAHRHEITEN, SOBALD JEMAND EINE DAVON
 * NACHZIEHT.** Genau das ist am 03.09.2026 passiert: der Fehler „das
 * Gedächtnis der Grenzen muss beim Ausblenden mit geleert werden" stand
 * zweimal da, und der Kommentar an der zweiten Stelle lautete wörtlich
 * „DERSELBE FEHLER WIE IM LAGEPLAN, und deshalb steht er zweimal da". Das ist
 * keine Begründung, sondern eine Ankündigung.
 *
 * **WAS DIE BEIDEN BEREICHE WIRKLICH UNTERSCHEIDET, SIND ZWEI DINGE** - und
 * Marvin nennt sie selbst:
 *
 *   1. **was beim Starten an ist** (`start`): der Lageplan geht an die
 *      Gemeinde und beginnt ohne Grenzen; die Planung zeichnet den Weg über
 *      fremden Grund und beginnt mit ihnen.
 *   2. **die Symbole zur Anzeichnung** (`werkzeuge` und `kinder`): dort ein
 *      roter Kreis und ein roter Weg für das Blatt an die Behörde, hier
 *      nummerierte Abschnitte in der Farbe ihrer Oberfläche.
 *
 * Alles andere steht hier, einmal.
 */

/**
 * Welcher Ausschnitt liegt vor dem Fenster? Dieselbe Rechnung wie im Server.
 *
 * SIE STAND ZWEIMAL DA, unter zwei Namen (`kasten` im Lageplan,
 * `kartenkasten` in der Planung), Zeichen für Zeichen gleich. Zwei Rahmen,
 * die um Bruchteile auseinanderlaufen, holen zwei verschiedene Kästen von
 * Grenzen - und dann zeigt die eine Karte ein Flurstück, das die andere nicht
 * hat. Dieselbe Lehre wie bei `ausschnittRahmen` auf dem Server.
 */
export function kartenkasten(l: Kartenlage, breite: number, hoehe: number) {
  const n = 2 ** l.zoom * 256;
  const s = Math.min(Math.max(Math.sin((l.mitte.lat * Math.PI) / 180), -0.9999),
                     0.9999);
  const mx = ((l.mitte.lng + 180) / 360) * n;
  const my = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n;
  const zurueck = (x: number, y: number) => {
    const t = Math.PI - (2 * Math.PI * y) / n;
    return {
      lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(t) - Math.exp(-t))),
      lng: (x / n) * 360 - 180,
    };
  };
  const lo = zurueck(mx - breite / 2, my - hoehe / 2);
  const ru = zurueck(mx + breite / 2, my + hoehe / 2);
  return {
    w: Math.min(lo.lng, ru.lng), o: Math.max(lo.lng, ru.lng),
    s: Math.min(lo.lat, ru.lat), n: Math.max(lo.lat, ru.lat),
  };
}

export default function Kartenflaeche({
  auftragId, lage, setzeLage, start, daten,
  wunschBreite = 820, wunschHoehe = 520,
  werkzeuge, rechts, aufKlick, aufProbe, kinder, aufSchalter,
  aufFlurstand,
}: {
  auftragId: string;
  lage: Kartenlage;
  setzeLage: (l: Kartenlage) => void;
  /** Was beim Öffnen angeschaltet ist. Der EINE Unterschied der Bereiche. */
  start: Kartenschalter;
  /** Die Wege vom Server: `luftbildweg`, `strassenweg`, … */
  daten: any;
  wunschBreite?: number;
  wunschHoehe?: number;
  /** Die Werkzeugknöpfe des Bereichs. Der ANDERE Unterschied. */
  werkzeuge?: React.ReactNode;
  /** Was rechts in der Leiste steht (Zahlen, Zustand). */
  rechts?: React.ReactNode;
  aufKlick?: (p: Punkt) => void;
  aufProbe?: (leser: ((p: Punkt) => number[] | null) | null) => void;
  kinder: (nach: (p: Punkt) => { x: number; y: number },
           mpp: number) => React.ReactNode;
  /**
   * Der Stand der Schalter, sobald er sich ändert.
   *
   * Der Lageplan schreibt `luftbild` und `flurstuecke` mit, wenn er den
   * Ausschnitt merkt; er braucht die Werte also. **Gehalten werden sie
   * trotzdem hier**, an EINER Stelle - der Empfänger führt eine Abschrift zum
   * Lesen und nicht eine zweite Wahrheit.
   */
  aufSchalter?: (s: Kartenschalter) => void;
  /** Ob gerade Grenzen geholt werden und was dabei herauskam. */
  aufFlurstand?: (s: { laeuft: boolean; meldung: string | null }) => void;
}) {
  /* GEMESSEN WIRD DIE SPALTE, IN DER DIE KARTE STEHT.
     ------------------------------------------------------------------------
     Ein `ResizeObserver` und nicht ein Zuhörer am Fenster: die Spalte ändert
     ihre Breite auch, wenn das Fenster gleich bleibt - beim Wechsel des
     Reiters oder wenn ein Hinweiskasten darüber dazukommt.

     ER HÄNGT AN `lage`, UND DAS IST KEINE FEINHEIT. Solange die Daten fehlen,
     zeichnet der Bereich darüber „Wird geladen"; den Kasten mit der Karte
     gibt es dann noch gar nicht, `platzfeld.current` ist `null`, und der
     Effekt kehrt um. Mit einer LEEREN Abhängigkeitsliste liefe er danach nie
     wieder, der Messfühler bliebe unangehängt und `platz` auf dem Wert des
     ersten Zeichnens stehen. Das hat am 04.09.2026 auf der Seite dazu
     geführt, dass die Karte sich über die Zahlen daneben legte. **Ein
     Effekt, dessen Element es beim ersten Zeichnen noch nicht gibt, braucht
     eine Abhängigkeit, die sagt, wann es da ist.** */
  const platzfeld = useRef<HTMLDivElement | null>(null);
  const [platz, setPlatz] = useState(0);
  useEffect(() => {
    const el = platzfeld.current;
    if (!el) return;
    const messen = () => setPlatz(el.clientWidth);
    messen();
    const b = new ResizeObserver(messen);
    b.observe(el);
    return () => b.disconnect();
  }, [!!daten]);
  const mass = kartenmass(platz, wunschBreite, wunschHoehe);

  const [luftbild, setLuftbild] = useState(start.luftbild);
  const [schrift, setSchrift] = useState(start.schrift);
  /* DER GEMERKTE WERT SCHALTET NICHTS EIN UND NICHTS AUS.
     ------------------------------------------------------------------------
     Hier stand einmal `setGrenzen(r.flurstuecke === true)` und
     `setLuftbild(r.luftbild !== false)`: die Werte kamen aus der
     gespeicherten Zeile. **Und in der steht ein ABDRUCK und keine
     Entscheidung** - er ist bei jedem „Ausschnitt merken" und bei jedem
     erzeugten Lageplan mitgeschrieben worden, auch dann, wenn niemand den
     Haken je angefasst hat.

     OB DAS LUFTBILD AUF DAS BLATT KOMMT, ÄNDERT SICH DABEI NICHT: dafür
     zählt der Stand IN DIESEM FENSTER, und den sieht der Innendienst vor
     sich. **Was gedruckt wird, entscheidet der Blick auf die Karte und nicht
     eine Spalte, die vor drei Wochen jemand anders gefüllt hat.** */
  const [grenzen, setGrenzen] = useState(start.grenzen);
  const [nummern, setNummern] = useState(start.nummern);

  const [flur, setFlur] = useState<Flurstueck[] | null>(null);
  /** Der Kasten, der zuletzt WIRKLICH geholt wurde. */
  const geholt = useRef<any>(null);

  /* Der Stand geht nach oben, sobald er sich ändert. Er wird hier gehalten;
     wer ihn braucht (der Lageplan beim Speichern), führt eine Abschrift zum
     Lesen. */
  useEffect(() => {
    aufSchalter?.({ luftbild, schrift, grenzen, nummern });
  }, [luftbild, schrift, grenzen, nummern]);

  /**
   * DIE GRENZEN NACHLADEN, ABER NUR WENN NÖTIG - UND ZWAR EINMAL IM HAUS.
   *
   * Der Server liefert den Kasten mit, den er WIRKLICH geholt hat: er liegt
   * auf einem Raster und ist grösser als der Ausschnitt. Solange das Fenster
   * darin bleibt, wird nicht gefragt. Eine Schätzung hier („wir haben sicher
   * etwas mehr") wäre geraten; die Angabe vom Server ist gemessen.
   *
   * AUSBLENDEN HEISST AUCH: DAS GEDÄCHTNIS LEEREN.
   *
   * ORIGINALTEXT MARVIN (25.08.2026)
   *   „ein fehler, wenn ich die grundstücksgrenzen einmal ausblende und dann
   *    wieder einblende erscheinen sie nicht wieder."
   *
   * `geholt.current` merkt sich den Kasten, damit beim Ziehen nicht bei jedem
   * Bildpunkt nachgefragt wird. Beim Ausblenden wurde `flur` geleert,
   * `geholt.current` blieb aber stehen; beim Wiedereinblenden sagte die
   * Abkürzung darunter „liegt schon im geholten Kasten" und kehrte zurück,
   * ohne etwas zu holen. Die Grenzen kamen nie wieder. **Ein
   * Zwischenspeicher, der einen Zustand überlebt, den er beschreibt, ist
   * keine Abkürzung mehr, sondern eine Behauptung.**
   */
  useEffect(() => {
    if (!grenzen) {
      setFlur(null); geholt.current = null;
      aufFlurstand?.({ laeuft: false, meldung: null });
      return;
    }
    const k = kartenkasten(lage, mass.breite, mass.hoehe);
    const alt = geholt.current;
    if (alt && k.w >= alt.w && k.o <= alt.o && k.s >= alt.s && k.n <= alt.n) {
      return;
    }

    let weg = false;
    const zeit = setTimeout(() => {
      aufFlurstand?.({ laeuft: true, meldung: null });
      api.flurstuecke(auftragId, k).then((r: any) => {
        if (weg) return;
        /* EIN FEHLSCHLAG NIMMT DIE ALTEN GRENZEN NICHT WEG. Wer ein Stück
           weiterzieht und der Dienst stolpert, behält, was er hatte, und
           liest den Grund daneben. */
        if (r.ok) setFlur(r.flurstuecke ?? []);
        if (r.kasten) geholt.current = r.kasten;
        aufFlurstand?.({ laeuft: false,
          meldung: r.ok ? (r.meldung ?? null)
                        : (r.meldung ?? 'Keine Grenzen.') });
      }).catch(() => {
        if (!weg) {
          aufFlurstand?.({ laeuft: false,
            meldung: 'Die Grenzen ließen sich nicht holen.' });
        }
      });
    }, 250);   // Beim Ziehen nicht bei jedem Bildpunkt fragen.
    return () => { weg = true; clearTimeout(zeit); };
  }, [lage.mitte.lat, lage.mitte.lng, lage.zoom, grenzen,
      mass.breite, mass.hoehe]);

  return (
    <>
      <div className="reihe" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {werkzeuge}

        <div className="wachsen" />

        {/* VIER HAKEN IN EINER REIHE, UND ZWAR IN BEIDEN BEREICHEN DIESELBEN.
         *
         * ORIGINALTEXT MARVIN (03.09.2026)
         *   „kannst du oben die checkboxen in eine reihe bringen."
         *
         * Sie standen zweimal im Haus, und die zweite Fassung hiess anders:
         * „Grenzen" statt „Grundstücksgrenzen", „Nummern" statt
         * „Flurstücksnummern". **Zwei Aufschriften für dasselbe Häkchen sehen
         * nach zwei Bildschirmen aus** - dieselbe Falle wie bei „Tb Fotos"
         * gegen „Tb fotos" und bei „orange/weiss" gegen „Orange/Weiß".
         *
         * Der Kasten bricht nicht um und rollt auf einem schmalen Bildschirm
         * (`.kartenschalter` in `stil.css`); dort steht auch, warum die
         * Zahlen nicht mehr als Inline-Stil an jedem Etikett stehen. */}
        <div className="kartenschalter">
          <label className="kartenhaken">
            <input type="checkbox" checked={luftbild}
                   onChange={(e) => setLuftbild(e.target.checked)} />
            Luftbild
          </label>
          {/* Marvin, 02.09.2026: „es fehlt aber der straßenname und die
              hausnummern." Nur über dem Luftbild: die Straßenkarte trägt ihre
              eigene Beschriftung, und zweimal übereinander ist sie
              unleserlich. */}
          <label className={'kartenhaken' + (luftbild ? '' : ' aus')}>
            <input type="checkbox" checked={luftbild && schrift}
                   disabled={!luftbild}
                   onChange={(e) => setSchrift(e.target.checked)} />
            Straßennamen
          </label>
          <label className="kartenhaken">
            <input type="checkbox" checked={grenzen}
                   onChange={(e) => setGrenzen(e.target.checked)} />
            Grundstücksgrenzen
          </label>
          {/* DIE NUMMERN GETRENNT VON DEN GRENZEN.
              Marvin, 25.08.2026: „ich will das die flurstücksnummern
              auszublenden gehen." Auf einer Innenstadtlage liegen
              zweihundertvierzig Flurstücke im Ausschnitt; die Grenzen helfen
              dann noch, die Nummern sind eine Wand aus Ziffern. */}
          <label className={'kartenhaken' + (grenzen ? '' : ' aus')}>
            <input type="checkbox" checked={grenzen && nummern}
                   disabled={!grenzen}
                   onChange={(e) => setNummern(e.target.checked)} />
            Flurstücksnummern
          </label>
        </div>

        {rechts}
      </div>

      <div ref={platzfeld}>
        <Tiefbaukarte
          lage={lage} setzeLage={setzeLage}
          breite={mass.breite} hoehe={mass.hoehe}
          luftbildweg={daten.luftbildweg}
          strassenweg={daten.strassenweg ?? null}
          beschriftungsweg={daten.beschriftungsweg ?? null}
          kartenbefund={daten.kartenbefund ?? null}
          beschriftung={schrift}
          luftbild={luftbild}
          aufLuftbild={setLuftbild}
          flurstuecke={grenzen ? flur : null}
          nummern={nummern}
          aufProbe={aufProbe}
          aufKlick={aufKlick}
          kinder={kinder}
        />
      </div>
    </>
  );
}
